import graphql__fcdb97750d7e6d6acf7a from "./__generated__/ContributionsDatavizGraphTheme_theme.graphql.js";import { useCallback, useRef } from "react";
import { graphql, useFragment } from "react-relay";
import { Outlet } from "react-router";

import { useContributionsDatavizContext } from "./ContributionsDataviz";
import useFilterableContributionsList from "../useFilterableContributionsList";
import { usePaginatedResults } from "../../../Utils/usePaginatedResults";
import { useFormatMessage } from "../../../Intl/Context";
import { intlMessages as rootLayoutIntlMessages } from "./ContributionsDatavizRootLayout";
import ContributionsDatavizThemeLayout from "./ContributionsDatavizThemeLayout";
import ContributionsDatavizGraphThemePagination from "./ContributionsDatavizGraphThemePagination";
import ContributionsDatavizGraphThemeAnimation from "./ContributionsDatavizGraphThemeAnimation";
import ContributionsDatavizMainContainerHeader from "./ContributionsDatavizMainContainerHeader";
import { useResourcesList } from "../../../ResourcesList/resourcesList";

const ContributionsDatavizGraphTheme = props => {
  const theme = useFragment(ContributionsDatavizGraphTheme_theme, props.theme);
  const { participativeModule } = theme;
  const { contributionsPerPage: resultsPerPage } = useContributionsDatavizContext();
  const resetPaginationCallbackRef = useRef();

  const resetPaginationAfterRefetched = useCallback(() => {
    const resetPaginationCallback = resetPaginationCallbackRef.current;

    if (resetPaginationCallback) resetPaginationCallback();
  }, []);

  const { contributions, loadMore, hasMoreToLoad } = useFilterableContributionsList({
    contribuable: props.theme,
    fragment: ContributionsDatavizGraphTheme_theme,
    contributionsBatchSize: resultsPerPage,
    onRefetchedAfterFiltersChanged: resetPaginationAfterRefetched,
    onRefetchedAfterSortingChanged: resetPaginationAfterRefetched
  });

  const {
    items: { count: totalResultsCount }
  } = useResourcesList();

  const { displayedResults, resetPagination, ...resultsLoader } = usePaginatedResults({
    results: contributions,
    loadMore,
    hasMoreToLoad,
    resultsPerPage,
    totalResultsCount
  });

  // Store the `resetPagination` callback to be used in our
  // `resetPaginationAfterRefetched` callback, as we can't provide directly the
  // `resetPagination` function to the `useFilterableContributionsList` hook, since it
  // must be called before the `usePaginatedResults` hook.
  //
  // Using a `useRef` instead of `useState` here allows updating the callback behavior
  // without expiring the dependencies of the callback, thus not re-rendering the
  // component and not expiring `useFilterableContributionsList` hook's inner dependencies
  // where the callback is used.
  resetPaginationCallbackRef.current = resetPagination;

  const t = useFormatMessage();
  const contributionsCountLabel = t(rootLayoutIntlMessages.contributionsCount, {
    count: totalResultsCount
  });

  const { pagesCount } = resultsLoader;

  return (
    <ContributionsDatavizThemeLayout
      theme={theme}
      graphViewActive
      filtersRefetchFragment={ContributionsDatavizGraphTheme_theme}
      filtersRefetchFragmentParent={props.theme}
      pagesCount={pagesCount}
    >
      <ContributionsDatavizMainContainerHeader
        participativeModule={participativeModule}
        theme={theme}
        graphViewActive
      >
        {pagesCount > 1 && (
          <ContributionsDatavizGraphThemePagination resultsLoader={resultsLoader} />
        )}

        {pagesCount <= 1 && [totalResultsCount, contributionsCountLabel].join(" ")}
      </ContributionsDatavizMainContainerHeader>

      <ContributionsDatavizGraphThemeAnimation
        theme={theme}
        contributions={displayedResults}
      />

      {/* Allow displaying contribution modal */}
      <Outlet />
    </ContributionsDatavizThemeLayout>
  );
};

const ContributionsDatavizGraphTheme_theme = graphql__fcdb97750d7e6d6acf7a;

export default ContributionsDatavizGraphTheme;
